'use client';

import { NativeSelect, NativeSelectOption } from '@svenskaspelab/ui/react/select';
import type { ReactNode } from 'react';

import { getFormattedCurrency } from '../../core/lib/entry.browser.js';
import type { WalletType } from '../../schema/frameworks/entry.js';
import type { ActivatedDrawId } from '../../schema/marketplace/entry.js';

export type PlayerWallet = {
  type: 'WALLET_PLAYER';
  balance: number;
  name: string;
};
export type MarketplaceWallet = {
  type: 'WALLET_MARKETPLACE_PLAYER';
  balance: number;
  activatedDrawId: ActivatedDrawId;
  name: string;
};

type DisabledWallet = {
  id: ActivatedDrawId;
  text: string;
};

type CouponAdditionsWalletSelectorProps = {
  wallets: (PlayerWallet | MarketplaceWallet)[];
  activatedDrawId?: ActivatedDrawId;
  onValueChange: (value: WalletType | ActivatedDrawId) => void;
  disabledWallets: DisabledWallet[];
  selectedCouponWallet?: WalletType | ActivatedDrawId;
};

/**
 * Component responsible for rendering the wallet selector in the coupon additions.
 *
 * @param root0 props
 * @param root0.wallets list of wallets to select from
 * @param root0.activatedDrawId activated draw id selected
 * @param root0.onValueChange callback for when the selected wallet changes
 * @param root0.disabledWallets list of wallets that should be disabled
 * @param root0.selectedCouponWallet the currently selected wallet for the coupon
 * @returns renders the wallet selector
 */
export function CouponAdditionsWalletSelector({
  wallets,
  activatedDrawId,
  onValueChange,
  disabledWallets,
  selectedCouponWallet,
}: CouponAdditionsWalletSelectorProps): ReactNode {
  const defaultValue =
    activatedDrawId && !disabledWallets.some((wallet) => wallet.id === activatedDrawId)
      ? `${activatedDrawId}`
      : 'WALLET_PLAYER';
  const selectedValue = selectedCouponWallet ? `${selectedCouponWallet}` : 'WALLET_PLAYER';

  return (
    <div className="flex">
      <NativeSelect
        defaultValue={defaultValue}
        aria-label="Välj spelkonto"
        aria-invalid={false}
        value={selectedValue}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const { value } = e.target;
          if (value === 'WALLET_PLAYER') {
            onValueChange(value);
            return;
          }
          // Base UI resets the value to `null` when the selected item is not (yet) rendered,
          // which would otherwise produce a NaN activated draw id.
          const parsedValue = Number.parseInt(value, 10);
          if (!Number.isFinite(parsedValue)) {
            return;
          }
          onValueChange(parsedValue as ActivatedDrawId);
        }}
      >
        {wallets.map((wallet, index) => {
          const isDisabled = disabledWallets.some(
            (disabledWallet) =>
              disabledWallet.id === (wallet.type === 'WALLET_MARKETPLACE_PLAYER' ? wallet.activatedDrawId : undefined)
          );
          return (
            <NativeSelectOption
              disabled={wallet.type === 'WALLET_MARKETPLACE_PLAYER' ? isDisabled : false}
              key={wallet.type === 'WALLET_MARKETPLACE_PLAYER' ? wallet.activatedDrawId : `wallet-${index}`}
              value={wallet.type === 'WALLET_MARKETPLACE_PLAYER' ? `${wallet.activatedDrawId}` : 'WALLET_PLAYER'}
            >
              {wallet.name} - {getFormattedCurrency(wallet.balance, { shouldUseDecimals: true })}
              {isDisabled &&
                ` (${disabledWallets.find((disabledWallet) => disabledWallet.id === (wallet.type === 'WALLET_MARKETPLACE_PLAYER' ? wallet.activatedDrawId : wallet.type))?.text})`}
            </NativeSelectOption>
          );
        })}
      </NativeSelect>
    </div>
  );
}
